import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Injector,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subject, asapScheduler, of } from 'rxjs';
import {
  takeUntil,
  filter,
  switchMap,
  timeout,
  catchError,
  delay,
  debounceTime,
} from 'rxjs/operators';

import { CitationService } from '../../services/citations.service';
import { CommonConstants } from '../../shared';
import { RHP_EDITOR_ID } from '../../modules/wk-document-decorator/const';

const {
  NUMBERED_CITATION,
  CK_PLUGINS: { ACCEPT_SUGGESTION, DISCARD_SUGGESTION },
} = CommonConstants;

// RDFa 'typeof' values wrapping unnumbered citation text (see citations.template.json); there is
// no dedicated model element for unnumbered citations, unlike NUMBERED_CITATION.
const UNNUM_CITATION_TYPEOF_RESOURCE = 'pcicore:BibliographicResource';
const UNNUM_CITATION_TYPEOF_REFERENCE = 'pcicore:BibliographicReference';
const DATA_CITATION_ID_ATTRIBUTE = 'data-citation-id';
const UNNUMBERED_CITATION_BLOCK_CLASS = 'unnum-block';

@Component({
  selector: 'wk-tc-cross-links-panel',
  templateUrl: './wk-tc-cross-links-panel.component.html',
  styleUrls: ['./wk-tc-cross-links-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'wk-tc-cross-links-panel' },
})
export class WkTcCrossLinksPanelComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private static readonly DOUBLE_ASTERISK_PLACEHOLDER =
    '___WK_DOUBLE_ASTERISK___';
  private static readonly OUTSIDE_CLICK_GUARD_MS = 120;
  private static readonly HIGHLIGHT_FOCUS_CLASS =
    'wk-crosslinks-focused-highlight';
  private readonly destroy$ = new Subject<void>();
  private readonly changeDetectorRef: ChangeDetectorRef;
  private readonly renderer: Renderer2;
  private readonly documentRef: Document;
  private readonly citationService: CitationService;
  private openedAt = 0;
  private pendingFocusedSuggestionId: string | null = null;
  private focusedSuggestionElement: HTMLElement | null = null;
  private preFocusedElement: HTMLElement | null = null;
  // ENHANCEMENT: Guard to prevent concurrent/overlapping refresh operations
  private isRefreshInProgress = false;
  private readonly suggestionThreadMap = new Map<string, string>();
  // BUGFIX: Track current editor instance to detect document changes
  private currentEditorInstanceId: string | null = null;
  @ViewChild('panelWrapper', { static: true })
  private readonly panelWrapperRef!: ElementRef<HTMLElement>;

  public isVisible = false;
  public trackChangesList: any[] = [];
  public activeChangeId: string | null = null;
  public replyingToSuggestionId: string | null = null;
  public replyDraft = '';
  public editingReplyId: string | null = null;
  public editReplyDraft = '';
  public replyAvatarInitials = 'ME';
  public isLoadingCitationType = false;

  constructor(private readonly injector: Injector) {
    this.changeDetectorRef =
      this.injector.get<ChangeDetectorRef>(ChangeDetectorRef);
    this.renderer = this.injector.get<Renderer2>(Renderer2);
    this.documentRef = this.injector.get<Document>(DOCUMENT);
    this.citationService = this.injector.get<CitationService>(CitationService);
    this.replyAvatarInitials = this.getCurrentUserInitials();
  }

  ngOnInit(): void {
    this.citationService.trackChangesClickedInMiniEditor
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshTrackChangesFromMiniEditor();
      });

    this.citationService.rhpTabChanged
      .pipe(
        takeUntil(this.destroy$),
        // BUGFIX-001: After tab change, wait for mini-editor to be registered for the active type
        switchMap(() => {
          // Show loading indicator when tab changes
          this.isLoadingCitationType = true;
          this.changeDetectorRef.markForCheck();
          // Clear focused suggestion when tab changes so panel uses the new citationEditType
          this.pendingFocusedSuggestionId = null;
          const activeCitationType = this.citationService.citationEditType;

          // Wait for miniEditorRegisteredForType$ to emit the active type
          // Timeout increased to 3500ms to accommodate debounceTime(500) in onReady() +
          // rendering + wk-citation initialization (if needed)
          return this.citationService.miniEditorRegisteredForType$.pipe(
            filter((registeredType) => registeredType === activeCitationType),
            timeout(3500), // Increased from 2500ms
            // BUGFIX-001: Use catchError to handle timeout without closing the main subscription
            // This allows subsequent rhpTabChanged events to be processed
            catchError(() => {
              // Return observable that emits, triggering the subscribe handler's next()
              return of(null);
            })
          );
        })
      )
      .subscribe(() => {
        // Fallback: Wait for editor content to be updated via bindRHPEditor
        // Since onReady() has debounceTime(500), content should be updated ~600-700ms after tab switch
        setTimeout(() => {
          this.refreshTrackChangesFromMiniEditor();
        }, 1000);
      });

    this.citationService.selectedTrackChangeId
      .pipe(takeUntil(this.destroy$))
      .subscribe((suggestionId) => {
        if (!suggestionId) {
          return;
        }

        this.pendingFocusedSuggestionId = suggestionId;

        // Wait for editor initialization before refreshing, same as tab-change handler.
        // This ensures suggestions are available even on first panel open before user clicks a tab.
        const activeCitationType = this.citationService.citationEditType;
        if (activeCitationType) {
          this.citationService.miniEditorRegisteredForType$
            .pipe(
              filter((registeredType) => registeredType === activeCitationType),
              timeout(3500),
              catchError(() => of(null)),
              takeUntil(this.destroy$),
              delay(1000)
            )
            .subscribe(() => {
              this.refreshTrackChangesFromMiniEditor();
            });
        } else {
          // If no citation type is active yet, still add delay to ensure editors are initialized
          asapScheduler.schedule(() => {
            this.refreshTrackChangesFromMiniEditor();
          }, 1000);
        }
      });

    this.citationService.changePanel$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshTrackChangesFromMiniEditor();
      });

    // BUGFIX: Monitor when mini editors are registered to detect new documents/citations
    // When a new mini editor is registered, it means we're now editing a different citation/document
    this.citationService.miniEditorRegisteredForType$
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe(() => {
        this.refreshTrackChangesFromMiniEditor();
      });
  }

  ngAfterViewInit(): void {
    const wrapper = this.panelWrapperRef?.nativeElement;
    if (wrapper && wrapper.parentElement !== this.documentRef.body) {
      this.renderer.appendChild(this.documentRef.body, wrapper);
    }
  }

  ngOnDestroy(): void {
    this.clearFocusedSuggestionHighlight();
    const wrapper = this.panelWrapperRef?.nativeElement;
    if (wrapper?.parentElement === this.documentRef.body) {
      this.renderer.removeChild(this.documentRef.body, wrapper);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Process track changes data into a displayable list
   */
  private refreshTrackChangesFromMiniEditor(): void {
    const currentEditor = this.getMiniEditorsForPanel()[0];
    const currentEditorId = currentEditor?.id;

    if (currentEditorId !== this.currentEditorInstanceId) {
      this.trackChangesList = [];
      this.currentEditorInstanceId = currentEditorId;
      this.changeDetectorRef.markForCheck();
    }

    if (this.isRefreshInProgress) {
      return;
    }

    this.isRefreshInProgress = true;
    try {
      this.refreshPanelContents();
    } finally {
      this.isRefreshInProgress = false;
    }
  }

  private refreshPanelContents(): void {
    const miniEditors = this.getMiniEditorsForPanel();
    // Refreshes RHP sidebar with updated trackchanges when
    // minieditor is opened/closed
    // 'WideSidebar' option in dropdown is clicked on document load

    if (!miniEditors || miniEditors.length === 0) {
      this.trackChangesList = [];
      this.suggestionThreadMap.clear();
      this.pendingFocusedSuggestionId = null;
      this.clearFocusedSuggestionHighlight();
      this.isLoadingCitationType = false;
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.suggestionThreadMap.clear();
    this.clearMissingPendingFocus(miniEditors);

    const changes: ReturnType<
      WkTcCrossLinksPanelComponent['collectChangesFromEditor']
    > = [];
    miniEditors.forEach((editor) => {
      this.collectChangesFromEditor(editor).forEach((change) =>
        changes.push(change)
      );
    });
    const seenTypesById = new Map<string, Set<string>>();
    const uniqueChanges = changes.filter((change) => {
      const seenTypes = seenTypesById.get(change.id) || new Set<string>();
      if (seenTypes.has(change.type)) {
        return false;
      }
      seenTypes.add(change.type);
      seenTypesById.set(change.id, seenTypes);
      return true;
    });

    const displayChanges =
      this.collapseReplacementSuggestionChains(uniqueChanges);
    this.trackChangesList = this.groupChangesByCitationId(displayChanges);
    this.sortChangeReplies();
    this.focusPendingSuggestion();
    this.isLoadingCitationType = false;
    this.changeDetectorRef.markForCheck();
  }

  private collapseReplacementSuggestionChains(changes: any[]): any[] {
    const visibleChanges: any[] = [];
    const processedChanges = new Set<any>();
    const changesByEditor = new Map<
      object,
      Map<string, (typeof changes)[number]>
    >();
    const editingPluginsByEditor = new Map<object, object | null>();

    changes.forEach((change) => {
      if (change.type === 'comment') {
        return;
      }
      const byId =
        changesByEditor.get(change.sourceEditor) ||
        new Map<string, (typeof changes)[number]>();
      byId.set(change.id, change);
      changesByEditor.set(change.sourceEditor, byId);
    });

    changes.forEach((change) => {
      if (processedChanges.has(change)) {
        return;
      }

      const changesById = changesByEditor.get(change.sourceEditor) || new Map();
      if (!editingPluginsByEditor.has(change.sourceEditor)) {
        try {
          editingPluginsByEditor.set(
            change.sourceEditor,
            change.sourceEditor?.plugins?.get?.('TrackChangesEditing')
          );
        } catch (_error) {
          editingPluginsByEditor.set(change.sourceEditor, null);
        }
      }

      const chainChanges = this.getChainSuggestionItems(
        change,
        changesById,
        editingPluginsByEditor.get(change.sourceEditor)
      );
      const deletion = chainChanges.find((item) => item?.type === 'deletion');
      const insertion = chainChanges.find((item) => item?.type === 'insertion');

      if (!deletion || !insertion) {
        processedChanges.add(change);
        visibleChanges.push(change);
        return;
      }

      const replacementChanges = chainChanges.filter(
        (item) => item?.type === 'deletion' || item?.type === 'insertion'
      );
      deletion.suggestionIds = [
        deletion.id,
        ...replacementChanges
          .map((item) => item.id)
          .filter((id) => id !== deletion.id),
      ];
      replacementChanges.forEach((item) => processedChanges.add(item));
      visibleChanges.push(deletion);
    });

    return visibleChanges;
  }

  private clearMissingPendingFocus(editors: any[]): void {
    if (!this.pendingFocusedSuggestionId) {
      return;
    }

    const suggestionExists = editors.some((editor) => {
      const trackChangesPlugin = editor?.plugins?.get?.('TrackChanges');
      const editingPlugin = editor?.plugins?.get?.('TrackChangesEditing');
      return (
        editingPlugin?.getSuggestion?.(this.pendingFocusedSuggestionId) ||
        trackChangesPlugin?.getSuggestion?.(this.pendingFocusedSuggestionId)
      );
    });
    if (!suggestionExists) {
      this.pendingFocusedSuggestionId = null;
    }
  }

  private collectChangesFromEditor(editor: any): any[] {
    const trackChangesPlugin = editor?.plugins?.get?.('TrackChanges');
    const trackChangesEditingPlugin = editor?.plugins?.get?.(
      'TrackChangesEditing'
    );
    const commentsRepository = editor?.plugins?.get?.('CommentsRepository');
    const suggestions = this.getSuggestionsForEditor(
      editor,
      trackChangesPlugin,
      trackChangesEditingPlugin
    );
    const suggestionChanges = this.createSuggestionChanges(
      editor,
      suggestions,
      trackChangesPlugin,
      trackChangesEditingPlugin,
      commentsRepository
    );

    this.appendCommentThreadChanges(
      editor,
      commentsRepository,
      suggestionChanges.suggestionIds,
      suggestionChanges.byId,
      suggestionChanges.changes
    );
    return suggestionChanges.changes;
  }

  private getSuggestionsForEditor(
    editor: any,
    trackChangesPlugin: any,
    editingPlugin: any
  ): any[] {
    const suggestions = this.getMiniEditorSuggestions(
      trackChangesPlugin,
      editingPlugin
    );
    const editorData = editor?.getData?.();
    return typeof editorData === 'string'
      ? suggestions.filter(
          (suggestion) => !!suggestion?.id && editorData.includes(suggestion.id)
        )
      : [];
  }

  private createSuggestionChanges(
    editor: any,
    suggestions: any[],
    trackChangesPlugin: any,
    editingPlugin: any,
    commentsRepository: any
  ): { changes: any[]; suggestionIds: string[]; byId: Map<string, any> } {
    const changes: any[] = [];
    const suggestionIds = suggestions
      .map((suggestion) => suggestion?.id)
      .filter((id) => !!id);
    const byId = new Map<string, any>();
    let citationOrderIndexes: Map<string, number> | undefined;

    suggestions.forEach((suggestion) => {
      const liveSuggestion =
        trackChangesPlugin?.getSuggestion?.(suggestion?.id) ||
        editingPlugin
          ?.getSuggestions?.()
          ?.find((item: any) => item?.id === suggestion?.id);
      const citationId = this.extractCitationIdFromSuggestion(
        liveSuggestion,
        editor
      );
      if (citationId && !citationOrderIndexes) {
        citationOrderIndexes = this.getCitationDocumentOrderIndexes(editor);
      }
      const suggestionChange: any = {
        id: suggestion.id,
        type: suggestion.type,
        content: this.getSuggestionDisplayContent(
          suggestion,
          trackChangesPlugin,
          editingPlugin
        ),
        timestamp: suggestion.createdAt,
        documentPosition: this.getSuggestionDocumentPosition(liveSuggestion),
        author: suggestion.authorId || 'Unknown',
        threadId: null,
        replies: [],
        citationId,
        citationOrderIndex: citationId
          ? this.getCitationDocumentOrderIndex(
              editor,
              citationId,
              citationOrderIndexes
            )
          : -1,
        sourceEditor: editor,
      };

      this.attachDirectSuggestionThread(
        suggestionChange,
        suggestion.id,
        commentsRepository
      );
      changes.push(suggestionChange);
      byId.set(suggestion.id, suggestionChange);
    });

    return { changes, suggestionIds, byId };
  }

  private getSuggestionDocumentPosition(suggestion: any): number[] | null {
    try {
      const path = suggestion?.getFirstRange?.()?.start?.path;
      return Array.isArray(path) &&
        path.every((segment) => Number.isInteger(segment))
        ? [...path]
        : null;
    } catch (_error) {
      return null;
    }
  }

  private attachDirectSuggestionThread(
    change: any,
    suggestionId: string,
    commentsRepository: any
  ): void {
    const thread = commentsRepository?.getCommentThread?.(suggestionId);
    const threadId = thread?.threadId || thread?.id;
    if (threadId) {
      change.threadId = threadId;
      this.suggestionThreadMap.set(suggestionId, threadId);
    }
  }

  private appendCommentThreadChanges(
    editor: any,
    commentsRepository: any,
    suggestionIds: string[],
    suggestionsById: Map<string, any>,
    changes: any[]
  ): void {
    let trackChangesEditingPlugin: any;
    try {
      trackChangesEditingPlugin = editor?.plugins?.get?.('TrackChangesEditing');
    } catch (_error) {
      trackChangesEditingPlugin = null;
    }

    const threads =
      commentsRepository?.getCommentThreads?.({
        skipNotAttached: true,
        skipEmpty: true,
        toJSON: true,
      }) || [];

    threads.forEach((thread) => {
      const suggestionId = this.resolveSuggestionIdForThread(
        thread?.threadId,
        suggestionIds
      );
      const parentSuggestion = suggestionId
        ? suggestionsById.get(suggestionId)
        : null;
      if (parentSuggestion && thread?.comments?.length > 0) {
        this.getChainSuggestionItems(
          parentSuggestion,
          suggestionsById,
          trackChangesEditingPlugin
        ).forEach((suggestion) =>
          this.attachRepliesToSuggestion(suggestion, thread)
        );
      } else if (thread?.comments?.length > 0) {
        changes.push(this.createStandaloneCommentChange(editor, thread));
      }
    });
  }

  private getChainSuggestionItems(
    parentSuggestion: any,
    suggestionsById: Map<string, any>,
    trackChangesEditingPlugin: any
  ): any[] {
    if (!parentSuggestion?.id || !trackChangesEditingPlugin?.getSuggestion) {
      return [parentSuggestion];
    }

    try {
      const liveSuggestion = trackChangesEditingPlugin.getSuggestion(
        parentSuggestion.id
      );
      if (!liveSuggestion) {
        return [parentSuggestion];
      }

      const adjacentSuggestions =
        typeof liveSuggestion.getAllAdjacentSuggestions === 'function'
          ? liveSuggestion.getAllAdjacentSuggestions() || []
          : [];
      const candidates = [
        liveSuggestion.head,
        ...adjacentSuggestions,
        liveSuggestion,
      ];
      const chainSuggestions = candidates
        .map((candidate) =>
          candidate?.id ? suggestionsById.get(candidate.id) : null
        )
        .filter((suggestion) => !!suggestion)
        .filter(
          (suggestion, index, allSuggestions) =>
            allSuggestions.findIndex((item) => item.id === suggestion.id) ===
            index
        );

      return chainSuggestions.length ? chainSuggestions : [parentSuggestion];
    } catch (_error) {
      return [parentSuggestion];
    }
  }

  private attachRepliesToSuggestion(suggestion: any, thread: any): void {
    suggestion.threadId = thread.threadId;
    this.suggestionThreadMap.set(suggestion.id, thread.threadId);
    suggestion.replies.push(...this.createThreadReplies(thread, 0));
  }

  private createStandaloneCommentChange(editor: any, thread: any): any {
    const firstComment = thread.comments[0];
    return {
      id: thread.threadId || firstComment?.commentId,
      type: 'comment',
      content: firstComment?.content || '',
      timestamp: firstComment?.createdAt
        ? new Date(firstComment.createdAt).toISOString()
        : new Date().toISOString(),
      author: firstComment?.authorId || 'Unknown',
      sourceEditor: editor,
      replies: this.createThreadReplies(thread, 1),
      citationId: null,
    };
  }

  private createThreadReplies(thread: any, startIndex: number): any[] {
    return thread.comments.slice(startIndex).map((comment, offset) => {
      const index = startIndex + offset;
      return {
        id:
          comment.commentId ||
          comment.id ||
          `${thread.threadId}-reply-${index}`,
        threadId: thread.threadId,
        sourceCommentId: comment.commentId || comment.id || null,
        sourceIndex: index,
        type: 'reply',
        content: comment.content || '',
        timestamp: comment.createdAt
          ? new Date(comment.createdAt).toISOString()
          : new Date().toISOString(),
        author: comment.authorId || 'Unknown',
      };
    });
  }

  private sortChangeReplies(): void {
    this.trackChangesList.forEach((change) => {
      if (Array.isArray(change?.replies) && change.replies.length > 0) {
        change.replies.sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
      }
    });
  }

  private focusPendingSuggestion(): void {
    if (!this.pendingFocusedSuggestionId) {
      return;
    }

    const suggestionId = this.pendingFocusedSuggestionId;
    this.pendingFocusedSuggestionId = null;
    this.focusChangeCardById(suggestionId, {
      openReply: false,
      shouldScroll: true,
    });
  }

  private getMiniEditorsForPanel(): any[] {
    const editor = this.citationService.rhpEditorInstance;
    return editor &&
      editor.state !== 'destroyed' &&
      editor.state !== 'initializing'
      ? [editor]
      : [];
  }

  private findEditorBySuggestionId(suggestionId: string): any {
    if (!suggestionId) {
      return null;
    }
    const editors = this.getMiniEditorsForPanel();
    return (
      editors.find((editor) => {
        try {
          const trackChangesPlugin = editor?.plugins?.get?.('TrackChanges');
          const trackChangesEditingPlugin = editor?.plugins?.get?.(
            'TrackChangesEditing'
          );

          if (
            trackChangesEditingPlugin?.getSuggestion?.(suggestionId) ||
            trackChangesPlugin?.getSuggestion?.(suggestionId)
          ) {
            return true;
          }

          const suggestions = this.getMiniEditorSuggestions(
            trackChangesPlugin,
            trackChangesEditingPlugin
          );
          return suggestions.some(
            (suggestion: any) => suggestion?.id === suggestionId
          );
        } catch (_error) {
          return false;
        }
      }) || null
    );
  }

  /**
   * ENHANCEMENT: Group changes by citation ID and flatten in citation document order.
   * This ensures track changes appear grouped by citation (1, 2, 3...) rather than by timestamp.
   * Within each citation group, changes are sorted chronologically.
   *
   * Returns flattened array: [Citation1_Change1, Citation1_Change2, Citation2_Change1, ...]
   */
  private groupChangesByCitationId(changes: any[]): any[] {
    const changesByCitation = new Map<string | null, any[]>();
    const citationOrder: (string | null)[] = [];

    // Group changes by citationId, preserving order of first appearance
    changes.forEach((change) => {
      const citationId = change.citationId || null;

      if (!changesByCitation.has(citationId)) {
        changesByCitation.set(citationId, []);
        citationOrder.push(citationId);
      }
      changesByCitation.get(citationId)?.push(change);
    });

    // Sort citations in document order (numbered citations 1, 2, 3... first, then orphaned)
    const citationOrderIndexes = new Map<string, number>();
    changes.forEach((change) => {
      if (
        change?.citationId &&
        Number.isInteger(change?.citationOrderIndex) &&
        change.citationOrderIndex >= 0
      ) {
        citationOrderIndexes.set(change.citationId, change.citationOrderIndex);
      }
    });
    const sortedCitationIds = [...citationOrder];
    sortedCitationIds.sort((a, b) =>
      this.compareCitationIds(a, b, citationOrderIndexes)
    );

    // Flatten groups in order: first all changes for Citation 1, then Citation 2, etc.
    const result: any[] = [];
    sortedCitationIds.forEach((citationId) => {
      /* istanbul ignore next -- every sorted id was inserted into changesByCitation above. */
      const changesForCitation = changesByCitation.get(citationId) || [];

      this.sortChangesWithinCitation(changesForCitation);

      result.push(...changesForCitation);
    });

    return result;
  }

  private sortChangesWithinCitation(changes: any[]): void {
    const firstEditor = changes[0]?.sourceEditor;
    const allPositionsComparable = changes.every(
      (change) =>
        change?.sourceEditor === firstEditor &&
        Array.isArray(change?.documentPosition)
    );

    changes.sort((a, b) => {
      if (allPositionsComparable) {
        const positionOrder = this.compareDocumentPositions(
          a.documentPosition,
          b.documentPosition
        );
        if (positionOrder !== 0) {
          return positionOrder;
        }
      }

      return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    });
  }

  private compareDocumentPositions(a: number[], b: number[]): number {
    const commonLength = Math.min(a.length, b.length);
    for (
      let positionIndex = 0;
      positionIndex < commonLength;
      positionIndex += 1
    ) {
      if (a[positionIndex] !== b[positionIndex]) {
        return a[positionIndex] - b[positionIndex];
      }
    }

    return a.length - b.length;
  }

  /**
   * Extract the citation ID/position from a suggestion's model items.
   * For numbered citations: extracts the position (1, 2, 3...)
   * For unnumbered citations: extracts position in order of appearance
   * Returns null if suggestion is not attached to any citation.
   */
  private extractCitationIdFromSuggestion(
    liveSuggestion: any,
    editor?: any
  ): string | null {
    try {
      if (!liveSuggestion) {
        return null;
      }

      const citationFromSuggestionDOM = this.findCitationForSuggestionViaDOM(
        editor,
        liveSuggestion.id
      );
      if (citationFromSuggestionDOM) {
        return citationFromSuggestionDOM;
      }

      // Strategy 1: Range-based lookup (most reliable)
      const citationFromRange = this.extractCitationViaRange(
        liveSuggestion,
        editor
      );
      if (citationFromRange) {
        return citationFromRange;
      }

      // Strategy 2: DOM-based approach
      const citationFromDOM = this.findCitationViaDOM(editor);
      if (citationFromDOM) {
        return citationFromDOM;
      }

      // Strategy 3: Text-based and model tree approaches
      const items = this.extractItemsFromSuggestion(liveSuggestion);

      // Check if first item's text node contains citation
      const textNodeCitation = this.extractCitationFromTextNode(items);
      if (textNodeCitation) {
        return textNodeCitation;
      }

      // Check if items text matches citation patterns
      const textCitation = this.extractCitationFromText(items);
      if (textCitation) {
        return textCitation;
      }

      // Search model tree for matching citations
      const modelCitation = this.extractCitationFromModelTree(items, editor);
      if (modelCitation) {
        return modelCitation;
      }

      // Search items directly for citation elements
      const itemsCitation = this.extractCitationFromItems(items);
      if (itemsCitation) {
        return itemsCitation;
      }

      return null;
    } catch (_error) {
      return null;
    }
  }

  /** Extract citation by checking suggestion's range position in the model */
  private extractCitationViaRange(
    liveSuggestion: any,
    editor: any
  ): string | null {
    if (!editor) {
      return null;
    }

    const suggestionRange = liveSuggestion?.getFirstRange?.();
    if (!suggestionRange) {
      return null;
    }

    return this.findCitationByRange(suggestionRange, editor);
  }

  /** Extract items array from suggestion using multiple fallback approaches */
  private extractItemsFromSuggestion(liveSuggestion: any): any[] {
    let items: any[] = [];

    // Approach 1: Try getItems() - standard CKEditor Track Changes API
    if (typeof liveSuggestion?.getItems === 'function') {
      try {
        const itemsResult = liveSuggestion.getItems();
        if (Array.isArray(itemsResult)) {
          items = itemsResult;
        } else if (
          itemsResult &&
          typeof itemsResult[Symbol.iterator] === 'function'
        ) {
          for (const item of itemsResult) {
            items.push(item);
          }
        }
      } catch (_err) {
        // Continue to fallback
      }
    }

    // Approach 2: Try internal data property
    if (items.length === 0 && Array.isArray(liveSuggestion?.data)) {
      items = liveSuggestion.data;
    }

    return items;
  }

  /** Extract citation from first item's text node */
  private extractCitationFromTextNode(items: any[]): string | null {
    const firstItemTextNode = items[0]?.textNode;
    if (!firstItemTextNode) {
      return null;
    }
    return this.findCitationFromTextNode(firstItemTextNode);
  }

  /** Extract citation ID by analyzing suggestion text with pattern matching */
  private extractCitationFromText(items: any[]): string | null {
    const suggestionText = this.getItemsText(items).toLowerCase().trim();

    // Try multiple citation reference patterns
    const citationPatterns = [
      /new\s+numbered\s+reference\s+(\d+)/i,
      /cited\s+reference\s+\[?(\d+)\]?/i,
      /reference\s+(\d+)/i,
      /\[(\d+)\]/,
      /reference\s+number\s+(\d+)/i,
    ];

    for (const pattern of citationPatterns) {
      const match = suggestionText.match(pattern);
      if (match && match[1]) {
        return `numbered-${match[1]}`;
      }
    }

    // Fallback: Check type indicators without specific number
    if (
      suggestionText.includes('numbered reference') &&
      !suggestionText.includes('unnumbered')
    ) {
      return 'numbered-1';
    }
    if (suggestionText.includes('unnumbered reference')) {
      return 'unnumbered-1';
    }

    return null;
  }

  /** Extract citation by searching the model tree */
  private extractCitationFromModelTree(
    items: any[],
    editor: any
  ): string | null {
    if (items.length === 0) {
      return null;
    }

    const textForSearch = this.getItemsText(items);
    const root = this.findModelRoot(items, editor);

    if (root) {
      return this.findCitationContainingText(root, textForSearch);
    }

    return null;
  }

  /** Find model root by traversing up parent chain */
  private findModelRoot(items: any[], editor: any): any {
    let root =
      items[0]?.root || items[0]?.getRoot?.() || editor?.model?.getRoot?.();

    if (!root && items[0]?.parent) {
      let current = items[0].parent;
      while (current?.parent) {
        current = current.parent;
      }
      root = current;
    }

    return root;
  }

  /** Extract citation from items by checking if they are citations or have citation ancestors */
  private extractCitationFromItems(items: any[]): string | null {
    for (const item of items) {
      if (!item) {
        continue;
      }

      // Check if item itself is a citation
      const directCitation = this.extractCitationIfIsItem(item);
      if (directCitation) {
        return directCitation;
      }

      // Traverse parent chain to find citation ancestor
      const ancestorCitation = this.extractCitationFromParentChain(item);
      if (ancestorCitation) {
        return ancestorCitation;
      }
    }

    return null;
  }

  /** Check if item is a citation and extract its ID */
  private extractCitationIfIsItem(item: any): string | null {
    if (this.isNumberedCitationItem(item)) {
      const citationNum = this.getCitationNumber(item);
      if (citationNum !== null) {
        return `numbered-${citationNum}`;
      }
    }

    if (this.isUnnumberedCitationItem(item)) {
      const position = this.getCitationNumber(item);
      if (position !== null) {
        return `unnumbered-${position}`;
      }
    }

    return null;
  }

  /** Walk up parent chain to find citation ancestor */
  private extractCitationFromParentChain(item: any): string | null {
    let parent = item?.parent;
    let depth = 0;
    const maxDepth = 10;

    while (parent && depth < maxDepth) {
      if (this.isNumberedCitationItem(parent)) {
        const citationNum = this.getCitationNumber(parent);
        if (citationNum !== null) {
          return `numbered-${citationNum}`;
        }
      }

      if (this.isUnnumberedCitationItem(parent)) {
        const position = this.getCitationNumber(parent);
        if (position !== null) {
          return `unnumbered-${position}`;
        }
      }

      parent = parent?.parent;
      depth++;
    }

    return null;
  }

  /** Extract text content from items array */
  private getItemsText(items: any[]): string {
    return items
      .map((item: any) => {
        if (item?.data) {
          return item.data;
        }
        if (item?.textNode?.data) {
          return item.textNode.data;
        }
        if (typeof item?.getText === 'function') {
          return item.getText();
        }
        return '';
      })
      .filter((text: string) => text.length > 0)
      .join(' ');
  }

  /**
   * Find citation via DOM inspection. Uses the rendered editor DOM to determine
   * which citation element contains the given model item's text.
   */
  /**
   * Given a text node from a suggestion, traverse UP through parent chain
   * to find the citation that contains this text node.
   */
  private findCitationFromTextNode(textNode: any): string | null {
    try {
      if (!textNode) {
        return null;
      }

      let current = textNode;
      let depth = 0;
      const maxDepth = 15; // Reasonable limit for tree depth

      while (current && depth < maxDepth) {
        // Check if current element is a citation
        if (this.isNumberedCitationItem(current)) {
          const citNum = this.getCitationNumber(current);
          return `numbered-${citNum}`;
        }

        if (this.isUnnumberedCitationItem(current)) {
          const citNum = this.getCitationNumber(current);
          return `unnumbered-${citNum}`;
        }

        // Move to parent
        current = current?.parent;
        depth++;
      }

      return null;
    } catch (_error) {
      return null;
    }
  }

  /**
   * Extract citation number/ID from a citation element.
   * Tries multiple attribute/property combinations to find the citation ID.
   */
  private getCitationNumber(element: any): string | null {
    if (!element) {
      return null;
    }

    // Try various ways to get the citation number
    const candidates = [
      element?.id,
      element?.getAttribute?.('id'),
      element?.getAttribute?.('data-id'),
      element?.getAttribute?.(DATA_CITATION_ID_ATTRIBUTE),
      element?.getAttribute?.('data-citation-num'),
      element?.getAttribute?.('data-position'),
      element?.index !== undefined ? String(element.index) : null,
    ];

    for (const candidate of candidates) {
      if (candidate && String(candidate).trim() !== '') {
        return String(candidate);
      }
    }

    return null;
  }

  /**
   * Find citation by checking which citation in the document contains the given range.
   * This uses the CKEditor model to find all citations and test range containment.
   */
  private findCitationByRange(
    suggestionRange: any,
    editor: any
  ): string | null {
    try {
      // Try different ways to access the model
      let model = editor?.model;
      if (!model) {
        model = editor?.editing?.model;
      }
      if (!model) {
        model = editor?.data?.model;
      }

      if (!model) {
        return null;
      }

      // Get the root element of the model
      let root = model.getRoot?.();
      if (!root) {
        // Try alternative ways to get root
        root = model.document?.getRoot?.();
      }
      if (!root) {
        root = model.root;
      }

      if (!root) {
        return null;
      }

      // Walk through the model tree to find all citations
      const citations = this.findAllCitationsInModel(root);

      // Check which citation contains this range
      for (const citation of citations) {
        if (
          this.isNumberedCitationItem(citation) ||
          this.isNumberedReferenceBlock(citation)
        ) {
          // For numbered citations, get their range from the model
          const citationRange = model.createRangeIn?.(citation);
          if (
            citationRange &&
            this.rangeContains(citationRange, suggestionRange)
          ) {
            const citNum = this.getCitationNumber(citation);

            return `numbered-${citNum}`;
          }
        } else if (
          this.isUnnumberedCitationItem(citation) ||
          this.isUnnumberedReferenceBlock(citation)
        ) {
          // For unnumbered citations
          const citationRange = model.createRangeIn?.(citation);
          if (
            citationRange &&
            this.rangeContains(citationRange, suggestionRange)
          ) {
            const citNum = this.getCitationNumber(citation);

            return `unnumbered-${citNum}`;
          }
        }
      }

      return null;
    } catch (_error) {
      return null;
    }
  }

  /**
   * Recursively walk the model tree to find all citation elements.
   */
  private findAllCitationsInModel(element: any): any[] {
    const citations: any[] = [];

    // Check if this element is a citation
    if (
      this.isNumberedCitationItem(element) ||
      this.isUnnumberedCitationItem(element) ||
      this.isNumberedReferenceBlock(element) ||
      this.isUnnumberedReferenceBlock(element)
    ) {
      citations.push(element);
    }

    // Recursively check children
    if (typeof element.getChildren === 'function') {
      for (const child of element.getChildren?.() || []) {
        citations.push(...this.findAllCitationsInModel(child));
      }
    }

    return citations;
  }

  /**
   * Check if rangeA contains rangeB (i.e., rangeB is fully inside rangeA).
   * Two ranges are compared by their start and end positions in the model tree.
   */
  private rangeContains(rangeA: any, rangeB: any): boolean {
    // This is a simplified check - CKEditor ranges may have complex comparison logic
    // For now, check if rangeB's start is >= rangeA's start and rangeB's end <= rangeA's end
    try {
      // Try to use CKEditor's built-in containment check if available
      if (typeof rangeA?.containsRange === 'function') {
        return rangeA.containsRange(rangeB);
      }

      // Fallback: compare positions manually
      const aStart = rangeA?.start?.path;
      const aEnd = rangeA?.end?.path;
      const bStart = rangeB?.start?.path;
      const bEnd = rangeB?.end?.path;

      if (!aStart || !aEnd || !bStart || !bEnd) {
        return false;
      }

      // Simple array comparison: convert paths to strings for comparison
      const aStartStr = aStart.join('-');
      const aEndStr = aEnd.join('-');
      const bStartStr = bStart.join('-');
      const bEndStr = bEnd.join('-');

      return aStartStr <= bStartStr && bEndStr <= aEndStr;
    } catch (_error) {
      return false;
    }
  }

  /**
   * Search the model tree for a citation element containing the given text.
   * Since citations are not ancestors of suggestions, we need to search all citations
   * and check which one's text content includes the suggestion text.
   */
  private findCitationContainingText(
    root: any,
    suggestionText: string
  ): string | null {
    if (!root || !suggestionText) {
      return null;
    }

    try {
      const textToFind = suggestionText.toLowerCase().trim();

      // Recursively collect all citations in the model
      const allCitations = this.findAllCitationsInModel(root);

      // Check each citation's text content
      for (const citation of allCitations) {
        try {
          // Get citation's text content
          let citationText = '';
          if (typeof citation.getText === 'function') {
            citationText = citation.getText().toLowerCase();
          } else if (citation.data) {
            citationText = citation.data.toLowerCase();
          } else if (typeof citation.getChildren === 'function') {
            // Build text from children
            const children = citation.getChildren?.() || [];
            const texts: string[] = [];
            for (const child of children) {
              if (child.data) {
                texts.push(child.data);
              } else if (typeof child.getText === 'function') {
                texts.push(child.getText());
              }
            }
            citationText = texts.join(' ').toLowerCase();
          }

          // Check if citation contains the suggestion text
          if (citationText.includes(textToFind)) {
            const citNum = this.getCitationNumber(citation);

            if (this.isNumberedCitationItem(citation)) {
              return `numbered-${citNum}`;
            }
            if (this.isUnnumberedCitationItem(citation)) {
              return `unnumbered-${citNum}`;
            }
          }
        } catch (_err) {
          continue;
        }
      }

      return null;
    } catch (_error) {
      return null;
    }
  }

  /**
   * FALLBACK APPROACH: Find citation using DOM-based detection.
   * Searches rendered HTML for citation elements that would contain the suggestion.
   * This works when model-based approaches fail because citations ARE rendered in the DOM.
   */
  private findCitationViaDOM(editor: any): string | null {
    try {
      if (!editor) {
        return null;
      }

      const editableElement =
        this.getEditableElement(editor) ||
        document.querySelector<HTMLElement>(
          '[contenteditable="true"][data-type="ckeditor"]'
        );

      if (!editableElement) {
        return null;
      }

      // Look for numbered citation elements with data-citation-number or id attribute
      // Also look for unnumbered citations with RDFa typeof attribute
      const citationSelectors = [
        `[${DATA_CITATION_ID_ATTRIBUTE}]`,
        '[data-model-name="numberedcitation"]',
        'span[data-citation-number]',
        'span[data-id][typeof*="BibliographicResource"]',
        'span[data-id][typeof*="BibliographicReference"]',
        '[typeof*="BibliographicResource"]',
        '[typeof*="BibliographicReference"]',
      ];

      let citationElements: HTMLElement[] = [];
      for (const selector of citationSelectors) {
        try {
          const found = Array.from(
            editableElement.querySelectorAll<HTMLElement>(selector)
          );
          citationElements.push(...found);
        } catch (_err) {
          throw new Error(
            `Failed to query citation elements with selector: ${selector}`
          );
        }
      }

      // Remove duplicates
      citationElements = Array.from(new Set(citationElements));

      if (citationElements.length === 0) {
        return null;
      }

      // Try to extract ID from first citation found (simple heuristic)
      for (const citationEl of citationElements) {
        const dataCitationId = citationEl.getAttribute(
          DATA_CITATION_ID_ATTRIBUTE
        );
        if (dataCitationId && citationEl.classList.contains('num-block')) {
          return `numbered-${dataCitationId}`;
        }
        if (
          dataCitationId &&
          citationEl.classList.contains(UNNUMBERED_CITATION_BLOCK_CLASS)
        ) {
          return `unnumbered-${dataCitationId}`;
        }

        // For numbered citations, look for id or data-citation-number
        let id =
          citationEl.getAttribute('id') ||
          citationEl.getAttribute('data-citation-number');
        const isNumbered =
          citationEl.getAttribute('data-model-name') === 'numberedcitation' ||
          !!citationEl.getAttribute('data-citation-number') ||
          /^\d+$/.test(id || '');

        if (id) {
          if (isNumbered) {
            return `numbered-${id}`;
          } else {
            return `unnumbered-${id}`;
          }
        }

        // For unnumbered citations, try to get data-id or generate from position
        const typeofAttr = citationEl.getAttribute('typeof');
        if (
          typeofAttr &&
          (typeofAttr.includes('BibliographicResource') ||
            typeofAttr.includes('BibliographicReference'))
        ) {
          id =
            citationEl.getAttribute('data-id') ||
            citationEl.getAttribute('id') ||
            citationEl.textContent?.substring(0, 10) ||
            'unknown';

          return `unnumbered-${id}`;
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  private getEditableElement(
    editor: CitationService['rhpEditorInstance']
  ): HTMLElement | null {
    return (
      editor?.editing?.view?.document?.getRoot?.()?.domElement ||
      editor?.ui?.view?.editable?.editableElement ||
      editor?.editing?.view?.getRoot?.()?.domElement ||
      null
    );
  }

  private findCitationForSuggestionViaDOM(
    editor: any,
    suggestionId: string
  ): string | null {
    if (!editor || !suggestionId) {
      return null;
    }

    const editableElement = this.getEditableElement(editor);
    if (!editableElement?.querySelectorAll) {
      return null;
    }

    const markerAttributes = [
      'data-suggestion',
      'data-insertion',
      'data-deletion',
      'data-suggestion-start-before',
      'data-suggestion-start-after',
      'data-suggestion-end-before',
      'data-suggestion-end-after',
    ];
    const markers = editableElement.querySelectorAll<HTMLElement>(
      markerAttributes.map((attribute) => `[${attribute}]`).join(',')
    );

    for (const marker of Array.from(markers)) {
      const belongsToSuggestion = markerAttributes.some((attribute) =>
        marker.getAttribute(attribute)?.includes(suggestionId)
      );
      if (!belongsToSuggestion) {
        continue;
      }

      const citationBlock = marker.closest<HTMLElement>(
        `[${DATA_CITATION_ID_ATTRIBUTE}]`
      );
      if (!citationBlock) {
        continue;
      }
      const citationId = citationBlock.getAttribute(DATA_CITATION_ID_ATTRIBUTE);
      if (!citationId) {
        continue;
      }
      if (citationBlock.classList.contains('num-block')) {
        return `numbered-${citationId}`;
      }
      if (citationBlock.classList.contains(UNNUMBERED_CITATION_BLOCK_CLASS)) {
        return `unnumbered-${citationId}`;
      }
    }

    return null;
  }

  /**
   * Compare two citation IDs for sorting in document order.
   * Numbered citations come first (sorted numerically), then unnumbered citations.
   * null citations (orphaned changes) come last.
   */
  private compareCitationIds(
    a: string | null,
    b: string | null,
    documentOrderIndexes?: Map<string, number>
  ): number {
    // null citations (orphaned) come last
    if (a === null && b === null) {
      return 0;
    }
    if (a === null) {
      return 1;
    }
    if (b === null) {
      return -1;
    }

    // Separate numbered and unnumbered
    const aIsNumbered = a.startsWith('numbered-');
    const bIsNumbered = b.startsWith('numbered-');

    // Numbered citations come before unnumbered
    if (aIsNumbered && !bIsNumbered) {
      return -1;
    }
    if (!aIsNumbered && bIsNumbered) {
      return 1;
    }

    const aDocumentIndex = documentOrderIndexes?.get(a);
    const bDocumentIndex = documentOrderIndexes?.get(b);
    if (aDocumentIndex !== undefined || bDocumentIndex !== undefined) {
      if (aDocumentIndex === undefined) {
        return 1;
      }
      if (bDocumentIndex === undefined) {
        return -1;
      }
      return aDocumentIndex - bDocumentIndex;
    }

    const aIndex = this.getCitationArrayIndex(a, aIsNumbered);
    const bIndex = this.getCitationArrayIndex(b, bIsNumbered);
    if (aIndex !== -1 || bIndex !== -1) {
      if (aIndex === -1) {
        return 1;
      }
      if (bIndex === -1) {
        return -1;
      }
      return aIndex - bIndex;
    }

    return a.localeCompare(b);
  }

  private getCitationDocumentOrderIndex(
    editor: any,
    citationId: string,
    citationOrderIndexes?: Map<string, number>
  ): number {
    return (
      (
        citationOrderIndexes || this.getCitationDocumentOrderIndexes(editor)
      ).get(citationId) ?? -1
    );
  }

  private getCitationDocumentOrderIndexes(editor: any): Map<string, number> {
    const indexes = new Map<string, number>();
    const root =
      editor?.model?.getRoot?.() ||
      editor?.model?.document?.getRoot?.() ||
      editor?.model?.root;
    if (!root) {
      return indexes;
    }

    let numberedIndex = 0;
    let unnumberedIndex = 0;
    this.findAllCitationsInModel(root).forEach((item) => {
      const isNumbered = this.isNumberedReferenceBlock(item);
      if (!isNumbered && !this.isUnnumberedReferenceBlock(item)) {
        return;
      }
      const position = isNumbered ? numberedIndex : unnumberedIndex;
      if (isNumbered) {
        numberedIndex += 1;
      } else {
        unnumberedIndex += 1;
      }
      const rawId = item?.getAttribute?.(DATA_CITATION_ID_ATTRIBUTE);
      const citationId = `${isNumbered ? 'numbered-' : 'unnumbered-'}${rawId}`;
      if (typeof rawId === 'string' && !indexes.has(citationId)) {
        indexes.set(citationId, position);
      }
    });
    return indexes;
  }

  private getCitationArrayIndex(
    citationId: string,
    isNumbered: boolean
  ): number {
    const prefix = isNumbered ? 'numbered-' : 'unnumbered-';
    const id = citationId.slice(prefix.length);
    const citations = isNumbered
      ? this.citationService.currentCitations?.references || []
      : this.citationService.currentCitations?.unnumbered_references || [];

    const matchingIndex = citations.findIndex(
      (citation) =>
        citation?.citationId === id || (!isNumbered && citation?.guidbr === id)
    );
    if (matchingIndex !== -1) {
      return matchingIndex;
    }

    const numericPosition = Number(id);
    return Number.isInteger(numericPosition) && numericPosition > 0
      ? numericPosition - 1
      : -1;
  }

  // Numbered citations are represented by a dedicated 'numberedcitation' model element.
  private isNumberedCitationItem(item: any): boolean {
    return item?.name === NUMBERED_CITATION;
  }

  private isNumberedReferenceBlock(item: any): boolean {
    const className =
      item?.getAttribute?.('class') || item?._attrs?.get?.('class') || '';
    return (
      className.includes('num-block') &&
      !className.includes(UNNUMBERED_CITATION_BLOCK_CLASS)
    );
  }

  private isUnnumberedReferenceBlock(item: any): boolean {
    const className =
      item?.getAttribute?.('class') || item?._attrs?.get?.('class') || '';
    return className.includes(UNNUMBERED_CITATION_BLOCK_CLASS);
  }

  // Unnumbered citations have no dedicated element; identified by the RDFa 'typeof' attribute.
  private isUnnumberedCitationItem(item: any): boolean {
    return (
      item?.name === 'span' &&
      (item?.getAttribute?.('typeof') === UNNUM_CITATION_TYPEOF_RESOURCE ||
        item?.getAttribute?.('typeof') === UNNUM_CITATION_TYPEOF_REFERENCE)
    );
  }

  private getMiniEditorSuggestions(
    trackChangesPlugin: any,
    trackChangesEditingPlugin: any
  ): any[] {
    try {
      if (typeof trackChangesPlugin?.getSuggestions === 'function') {
        return (
          trackChangesPlugin.getSuggestions({
            skipNotAttached: true,
            toJSON: true,
            skipEmpty: true,
          }) || []
        );
      }

      if (trackChangesEditingPlugin?.getSuggestions) {
        const suggestions = trackChangesEditingPlugin.getSuggestions() || [];
        return suggestions.map((suggestion: any) =>
          typeof suggestion?.toJSON === 'function'
            ? suggestion.toJSON()
            : {
                id: suggestion?.id,
                type: suggestion?.type,
                data: suggestion?.data,
                createdAt: suggestion?.createdAt,
                authorId: suggestion?.authorId,
              }
        );
      }
    } catch (_error) {
      return [];
    }

    return [];
  }

  private getSuggestionDisplayContent(
    suggestion: any,
    trackChangesPlugin: any,
    trackChangesEditingPlugin: any
  ): string {
    try {
      const descriptionFactory = trackChangesEditingPlugin?.descriptionFactory;
      const liveSuggestion =
        trackChangesPlugin?.getSuggestion?.(suggestion?.id) ||
        trackChangesEditingPlugin
          ?.getSuggestions?.()
          ?.find((item: any) => item?.id === suggestion?.id);

      if (descriptionFactory?.getDescriptions && liveSuggestion) {
        const adjacentSuggestions =
          typeof liveSuggestion.getAllAdjacentSuggestions === 'function'
            ? liveSuggestion.getAllAdjacentSuggestions()
            : [liveSuggestion];
        const descriptions =
          descriptionFactory.getDescriptions(adjacentSuggestions) || [];
        const descriptionWithContent = descriptions.find(
          (item: any) =>
            typeof item?.content === 'string' && item.content.trim()
        );
        if (descriptionWithContent?.content) {
          return descriptionWithContent.content;
        }
      }
    } catch (_error) {
      // Fallback below.
    }

    if (typeof suggestion?.data === 'string' && suggestion.data.trim()) {
      return suggestion.data;
    }

    if (suggestion?.data && typeof suggestion.data === 'object') {
      if (
        typeof suggestion.data.content === 'string' &&
        suggestion.data.content.trim()
      ) {
        return suggestion.data.content;
      }
      if (
        typeof suggestion.data.text === 'string' &&
        suggestion.data.text.trim()
      ) {
        return suggestion.data.text;
      }
      return JSON.stringify(suggestion.data);
    }

    return 'No content';
  }

  private resolveSuggestionIdForThread(
    threadId: string,
    suggestionIds: string[]
  ): string | null {
    if (!threadId || !suggestionIds?.length) {
      return null;
    }

    if (suggestionIds.includes(threadId)) {
      return threadId;
    }

    const idMatch = suggestionIds.find(
      (suggestionId) =>
        threadId.includes(suggestionId) || suggestionId.includes(threadId)
    );

    return idMatch || null;
  }

  /**
   * Accept a track change
   */
  public acceptChange(changeId: string): void {
    this.executeSuggestionCommand(ACCEPT_SUGGESTION, changeId);
  }

  /**
   * Reject a track change
   */
  public rejectChange(changeId: string): void {
    this.executeSuggestionCommand(DISCARD_SUGGESTION, changeId);
  }

  public acceptAllChanges(): void {
    this.executeBulkSuggestionCommand(ACCEPT_SUGGESTION);
  }

  public rejectAllChanges(): void {
    this.executeBulkSuggestionCommand(DISCARD_SUGGESTION);
  }

  public hasActionableChanges(): boolean {
    return this.trackChangesList.some(
      (change) => change?.type !== 'comment' && !!change?.id
    );
  }

  private markDocumentChanged(): void {
    this.citationService.shouldSaveContentData = true;
    this.citationService.editorContentChanged.next();
  }

  public startReply(changeId: string): void {
    this.activeChangeId = changeId;
    this.replyingToSuggestionId = changeId;
    this.replyDraft = '';
    this.changeDetectorRef.markForCheck();
  }

  public setActiveChange(change: any): void {
    const changeId = change?.id;
    if (!changeId) {
      return;
    }

    this.focusChangeCardById(changeId, {
      openReply: true,
      shouldScroll: false,
    });
    this.focusSuggestionInEditor(changeId);
  }

  private focusChangeCardById(
    changeId: string,
    options: { openReply: boolean; shouldScroll: boolean }
  ): void {
    const selectedChange = this.trackChangesList.find(
      (change) =>
        change?.id === changeId || change?.suggestionIds?.includes(changeId)
    );
    if (!selectedChange) {
      return;
    }

    const cardId = selectedChange.id;
    this.activeChangeId = cardId;

    if (options.openReply && selectedChange.type !== 'comment') {
      if (this.replyingToSuggestionId !== cardId) {
        this.replyDraft = '';
      }
      this.replyingToSuggestionId = cardId;
    } else {
      this.replyingToSuggestionId = null;
      this.replyDraft = '';
    }

    this.changeDetectorRef.markForCheck();

    if (options.shouldScroll) {
      this.scrollToChangeCard(cardId);
    }
  }

  private scrollToChangeCard(changeId: string): void {
    setTimeout(() => {
      const cards = Array.from(
        this.documentRef.querySelectorAll(
          '.wk-tc-cross-links-panel .change-item[data-change-id]'
        )
      );
      const targetCard = cards.find(
        (card) => card.getAttribute('data-change-id') === changeId
      );
      targetCard?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  private focusSuggestionInEditor(changeId: string): void {
    this.clearFocusedSuggestionHighlight();

    const selectors = [
      `[data-suggestion='${changeId}']`,
      `[data-insertion='${changeId}']`,
      `[data-deletion='${changeId}']`,
      `[data-suggestion-start-before*='${changeId}']`,
      `[data-suggestion-start-after*='${changeId}']`,
      `[data-suggestion-end-before*='${changeId}']`,
      `[data-suggestion-end-after*='${changeId}']`,
      `[data-comment-thread-id='${changeId}']`,
    ];

    let suggestionElement: HTMLElement | null = null;

    for (const editorInstance of this.getSuggestionEditorCandidates(changeId)) {
      const mainRoot = editorInstance?.editing?.view?.domRoots?.get?.(
        'main'
      ) as HTMLElement;
      if (!mainRoot) {
        continue;
      }

      const candidateElement = selectors
        .map((selector) => mainRoot.querySelector<HTMLElement>(selector))
        .find((element): element is HTMLElement => !!element);

      if (!candidateElement) {
        continue;
      }

      // Collapsing/expanding the RHP leaves destroyed editors behind whose roots are detached from
      // the page; scrolling into those is a silent no-op, so keep looking for a live editor first.
      if (this.documentRef.contains(mainRoot)) {
        suggestionElement = candidateElement;
        break;
      }

      suggestionElement = suggestionElement || candidateElement;
    }

    if (!suggestionElement) {
      return;
    }

    suggestionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    suggestionElement.classList.add(
      WkTcCrossLinksPanelComponent.HIGHLIGHT_FOCUS_CLASS
    );
    this.focusedSuggestionElement = suggestionElement;
  }

  private getSuggestionEditorCandidates(changeId: string): any[] {
    const sourceEditor = this.trackChangesList.find(
      (change) => change?.id === changeId
    )?.sourceEditor;

    return this.getUniqueEditors([
      sourceEditor,
      this.findEditorBySuggestionId(changeId),
      this.citationService.miniEditorInstance,
      this.citationService.rhpEditorInstance,
    ]);
  }

  private getUniqueEditors<T>(editors: Array<T | null | undefined>): T[] {
    return Array.from(
      new Set(editors.filter((editor): editor is T => !!editor))
    );
  }

  private clearFocusedSuggestionHighlight(): void {
    if (!this.focusedSuggestionElement) {
      return;
    }

    this.focusedSuggestionElement.classList.remove(
      WkTcCrossLinksPanelComponent.HIGHLIGHT_FOCUS_CLASS
    );
    this.focusedSuggestionElement = null;
  }

  public cancelReply(): void {
    this.replyingToSuggestionId = null;
    this.replyDraft = '';
    this.changeDetectorRef.markForCheck();
  }

  public isEditingReply(reply: any): boolean {
    return this.editingReplyId === reply?.id;
  }

  public startEditReply(reply: any, event?: Event): void {
    event?.stopPropagation();
    if (!reply?.id) {
      return;
    }

    this.editingReplyId = reply.id;
    this.editReplyDraft = this.getPlainTextFromHtml(reply.content || '');
    this.changeDetectorRef.markForCheck();
  }

  public cancelEditReply(event?: Event): void {
    event?.stopPropagation();
    this.editingReplyId = null;
    this.editReplyDraft = '';
    this.changeDetectorRef.markForCheck();
  }

  public saveReplyEdit(change: any, reply: any, event?: Event): void {
    event?.stopPropagation();
    const content = this.editReplyDraft?.trim();

    if (!content) {
      return;
    }

    const context = this.findThreadContext(change?.id, reply?.threadId);
    if (!context?.thread) {
      return;
    }

    try {
      const threadId =
        context.thread?.threadId || context.thread?.id || reply?.threadId;
      const threadForUpdate =
        this.findSerializableThreadContext(
          context.commentsRepository,
          threadId,
          change?.id
        ) || context.thread;
      const targetComment = this.findCommentByReply(threadForUpdate, reply);

      if (!targetComment) {
        return;
      }

      const normalizedContent = this.wrapTextAsParagraph(content);

      // Update the comment directly in the thread (the UPDATE_COMMENT command may not exist in this build)
      targetComment.content = normalizedContent;
      context.commentsRepository?.updateCommentThread?.(threadForUpdate);

      // Optimistically reflect the edited content in the panel row
      reply.content = normalizedContent;
      this.markDocumentChanged();

      this.editingReplyId = null;
      this.editReplyDraft = '';

      // Trigger change detection and update display without full refresh (avoid overwriting with stale editor data)
      this.changeDetectorRef.markForCheck();
    } catch (error) {
      console.error('Error saving reply edit:', error);
    }
  }

  public deleteReply(change: any, reply: any, event?: Event): void {
    event?.stopPropagation();

    const context = this.findThreadContext(change?.id, reply?.threadId);
    if (!context?.thread) {
      return;
    }

    try {
      const threadId =
        context.thread?.threadId || context.thread?.id || reply?.threadId;
      const threadForUpdate =
        this.findSerializableThreadContext(
          context.commentsRepository,
          threadId,
          change?.id
        ) || context.thread;
      const targetComment = this.findCommentByReply(threadForUpdate, reply);
      const commentId =
        targetComment?.commentId ||
        targetComment?.id ||
        reply?.sourceCommentId ||
        reply?.id;
      const fallbackIndex =
        typeof reply?.sourceIndex === 'number' ? reply.sourceIndex : -1;

      if (!threadId || !commentId) {
        return;
      }

      this.executeRemoveComment(context.editor, threadId, commentId);

      // Keep live editor model and serialized repository thread in sync.
      this.removeCommentFromThread(context.thread, commentId, fallbackIndex);
      const removedInThread = this.removeCommentFromThread(
        threadForUpdate,
        commentId,
        fallbackIndex
      );
      if (removedInThread && context.commentsRepository?.updateCommentThread) {
        context.commentsRepository?.updateCommentThread?.(threadForUpdate);
      }

      if (
        this.editingReplyId === reply?.id ||
        this.editingReplyId === reply?.sourceCommentId
      ) {
        this.editingReplyId = null;
        this.editReplyDraft = '';
      }

      this.markDocumentChanged();
      setTimeout(() => {
        this.refreshTrackChangesFromMiniEditor();
      }, 60);
      this.changeDetectorRef.markForCheck();
    } catch (_error) {
      // Keep panel resilient if reply deletion fails for stale thread references.
    }
  }

  private executeRemoveComment(
    editor: any,
    threadId: string,
    commentId: string
  ): boolean {
    if (
      !editor?.commands?.get?.(CommonConstants.commentEvents.REMOVE_COMMENT)
    ) {
      return false;
    }

    const payloadAttempts: any[] = [
      { threadId, commentId },
      { commentId, threadId },
      { threadId, comment: commentId },
      { threadId, id: commentId },
    ];

    for (const payload of payloadAttempts) {
      try {
        editor.execute(CommonConstants.commentEvents.REMOVE_COMMENT, payload);
        return true;
      } catch (_error) {
        // Try other known payload shapes.
      }
    }

    const argsAttempts: Array<any[]> = [
      [threadId, commentId],
      [commentId, threadId],
      [commentId],
    ];

    for (const args of argsAttempts) {
      try {
        editor.execute(CommonConstants.commentEvents.REMOVE_COMMENT, ...args);
        return true;
      } catch (_error) {
        // Continue trying command signatures.
      }
    }

    return false;
  }

  public submitReply(changeId: string): void {
    const content = this.replyDraft?.trim();
    if (!content) {
      return;
    }

    const mappedThreadId = this.suggestionThreadMap.get(changeId) || changeId;
    const threadContext =
      this.findThreadContext(changeId, mappedThreadId) ||
      this.findThreadContext(changeId, changeId);

    const editorInstance =
      this.citationService?.rhpEditorInstance ||
      threadContext?.editor ||
      this.findEditorBySuggestionId(changeId);
    const commentsRepository =
      threadContext?.commentsRepository ||
      editorInstance?.plugins?.get?.('CommentsRepository');
    const resolvedThread =
      threadContext?.thread ||
      this.findCommentThreadForSuggestion(commentsRepository, changeId);
    const thread = resolvedThread?.isAttached
      ? resolvedThread
      : this.findAttachedThreadForSuggestion(commentsRepository, changeId) ||
        resolvedThread;

    if (!thread) {
      return;
    }

    const currentUser = this.getCurrentUserId();
    const commentPayload = {
      commentId: `reply-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 10)}`,
      content: this.wrapTextAsParagraph(content),
      createdAt: new Date().toISOString(),
      authorId: currentUser,
      attributes: {},
    };

    try {
      if (typeof thread.addComment === 'function') {
        thread.addComment(commentPayload);
      } else {
        thread.comments = [...(thread.comments || []), commentPayload];
        commentsRepository?.updateCommentThread?.(thread);
      }

      this.markDocumentChanged();
      this.replyingToSuggestionId = null;
      this.replyDraft = '';
      this.refreshTrackChangesFromMiniEditor();
      this.changeDetectorRef.markForCheck();
    } catch (_error) {
      // Keep panel stable if reply cannot be posted for stale thread references.
    }
  }

  /**
   * A replaced text is stored as a suggestion chain (deletion + insertion). Only one suggestion of
   * the chain owns the attached comment thread; the other one resolves to a detached thread whose
   * comments are never rendered or persisted. Walk the chain to find the attached thread.
   */
  private findAttachedThreadForSuggestion(
    commentsRepository: any,
    changeId: string
  ): any {
    if (!changeId) {
      return null;
    }

    try {
      const editorInstance = this.citationService?.rhpEditorInstance;
      const liveSuggestion =
        editorInstance?.plugins
          ?.get?.('TrackChangesEditing')
          ?.getSuggestion?.(changeId) ||
        editorInstance?.plugins
          ?.get?.('TrackChanges')
          ?.getSuggestion?.(changeId);

      if (!liveSuggestion) {
        return null;
      }

      const adjacentSuggestions =
        typeof liveSuggestion.getAllAdjacentSuggestions === 'function'
          ? liveSuggestion.getAllAdjacentSuggestions() || []
          : [];
      const candidates = [
        liveSuggestion.head,
        ...adjacentSuggestions,
        liveSuggestion,
      ].filter((item) => !!item);

      for (const candidate of candidates) {
        const thread =
          candidate.commentThread ||
          commentsRepository?.getCommentThread?.(candidate.id);
        if (thread?.isAttached) {
          return thread;
        }
      }
    } catch (_error) {
      // Fall back to the caller's resolved thread when the suggestion id is stale.
    }

    return null;
  }

  /**
   * AcceptSuggestionCommand/DiscardSuggestionCommand compute `isEnabled` from the editor's
   * current model selection (they must be positioned at/within the suggestion to be enabled).
   * An inline click on a suggestion marker naturally moves the selection there first; triggering
   * accept/reject from this floating panel does not, so the command stays disabled and
   * `execute()` silently does nothing. Move the selection onto the suggestion's own range first
   * so the command is genuinely enabled before we execute it.
   */
  private moveSelectionToSuggestion(
    editorInstance: any,
    suggestion: any
  ): void {
    const range = suggestion?.getFirstRange?.();

    if (!range || typeof editorInstance?.model?.change !== 'function') {
      return;
    }

    try {
      editorInstance.model.change((writer) => {
        writer.setSelection(range);
      });
    } catch (_error) {
      // Keep floating panel resilient if the suggestion range is stale.
    }
  }

  private executeSuggestionCommand(
    commandName: string,
    suggestionId: string
  ): void {
    const change = this.trackChangesList.find(
      (item) =>
        item?.id === suggestionId || item?.suggestionIds?.includes(suggestionId)
    );
    const suggestionIds = change?.suggestionIds?.length
      ? change.suggestionIds
      : [suggestionId];

    if (!suggestionIds[0]) {
      return;
    }
    if (!this.citationService?.rhpEditorInstance) {
      return;
    }

    const editorInstance = this.citationService.rhpEditorInstance;
    let didExecute = false;
    const uniqueSuggestionIds = suggestionIds.filter(
      (id, index, ids) => ids.indexOf(id) === index
    );

    uniqueSuggestionIds.forEach((id) => {
      try {
        const trackChangesEditingPlugin = editorInstance.plugins?.get?.(
          'TrackChangesEditing'
        );
        const suggestion = trackChangesEditingPlugin?.getSuggestion?.(id);
        this.moveSelectionToSuggestion(editorInstance, suggestion);

        const command = editorInstance.commands?.get?.(commandName);
        if (command?.isEnabled) {
          editorInstance.execute(commandName, id);
          didExecute = true;
        } else if (commandName === ACCEPT_SUGGESTION && suggestion) {
          trackChangesEditingPlugin?.acceptSuggestion?.(suggestion);
          didExecute = true;
        }
      } catch (_error) {
        // Keep the panel responsive if one suggestion in a replacement chain is stale.
      }
    });

    if (!didExecute) {
      return;
    }

    this.markDocumentChanged();
    setTimeout(() => this.refreshTrackChangesFromMiniEditor(), 200);
  }

  private executeBulkSuggestionCommand(commandName: string): void {
    const actionableSuggestionIds = this.trackChangesList
      .filter((change) => change?.type !== 'comment' && !!change?.id)
      .map((change) => change.id);

    if (!actionableSuggestionIds.length) {
      return;
    }

    // Search for the editor containing the first actionable suggestion, then run
    // the editor-wide command once. Running it once avoids duplicate execution.
    if (!this.citationService?.rhpEditorInstance) {
      return;
    }

    const bulkCommand =
      commandName === ACCEPT_SUGGESTION
        ? 'acceptAllSuggestions'
        : 'discardAllSuggestions';
    this.citationService?.rhpEditorInstance.execute(bulkCommand, true);
    this.markDocumentChanged();

    // Refresh panel after giving CKEditor time to process all commands
    // Use 300ms to account for processing time with multiple suggestions
    setTimeout(() => {
      this.refreshTrackChangesFromMiniEditor();
    }, 300);
  }

  private findMiniEditorByThreadId(threadId: string): any {
    if (!threadId) {
      return null;
    }

    const miniEditors = this.getMiniEditorsForPanel();
    return (
      miniEditors.find((editor) => {
        try {
          const commentsRepository =
            editor?.plugins?.get?.('CommentsRepository');
          const threads = commentsRepository?.getCommentThreads?.() || [];
          return threads.some(
            (thread: any) =>
              thread?.threadId === threadId || thread?.id === threadId
          );
        } catch (_error) {
          return false;
        }
      }) || null
    );
  }

  private findCommentThreadForSuggestion(
    commentsRepository: any,
    suggestionId: string
  ): any {
    const threads = commentsRepository?.getCommentThreads?.() || [];
    if (!threads.length) {
      return null;
    }

    const directMatch = threads.find(
      (thread: any) =>
        thread?.threadId === suggestionId || thread?.id === suggestionId
    );
    if (directMatch) {
      return directMatch;
    }

    return (
      threads.find((thread: any) => {
        const threadId = thread?.threadId || thread?.id || '';
        return (
          threadId.includes(suggestionId) || suggestionId.includes(threadId)
        );
      }) || null
    );
  }

  private findThreadContext(
    changeId: string,
    threadId: string
  ): { editor: any; commentsRepository: any; thread: any } | null {
    const editor =
      this.citationService?.rhpEditorInstance ||
      this.findMiniEditorByThreadId(threadId) ||
      this.findEditorBySuggestionId(changeId) ||
      this.citationService.miniEditorInstance;
    const commentsRepository = editor?.plugins?.get?.('CommentsRepository');
    if (!commentsRepository) {
      return null;
    }

    const directThread =
      (threadId && commentsRepository?.getCommentThread?.(threadId)) ||
      (changeId && commentsRepository?.getCommentThread?.(changeId));
    if (directThread) {
      return { editor, commentsRepository, thread: directThread };
    }

    const threads = commentsRepository?.getCommentThreads?.() || [];
    const thread = this.findMatchingThread(threads, [threadId, changeId]);

    if (!thread) {
      return null;
    }

    return { editor, commentsRepository, thread };
  }

  private findSerializableThreadContext(
    commentsRepository: any,
    threadId: string,
    changeId: string
  ): any {
    if (!commentsRepository) {
      return null;
    }

    const serializableThreads =
      commentsRepository?.getCommentThreads?.({
        skipNotAttached: true,
        skipEmpty: false,
        toJSON: true,
      }) || [];

    if (serializableThreads.length) {
      return this.findMatchingThread(serializableThreads, [threadId, changeId]);
    }

    const fallbackThreads =
      commentsRepository?.getCommentThreads?.({
        skipNotAttached: false,
        skipEmpty: false,
        toJSON: true,
      }) || [];

    return this.findMatchingThread(fallbackThreads, [threadId, changeId]);
  }

  private findMatchingThread<T extends { threadId?: string; id?: string }>(
    threads: T[],
    targetIds: string[]
  ): T | null {
    return (
      threads.find((thread) => {
        const id = thread?.threadId || thread?.id || '';
        return targetIds.some(
          (target) =>
            !!target &&
            (id === target || id.includes(target) || target.includes(id))
        );
      }) || null
    );
  }

  private getThreadComments(thread: any): any[] {
    if (!thread?.comments) {
      return [];
    }

    if (Array.isArray(thread.comments)) {
      return thread.comments;
    }

    if (typeof thread.comments.toArray === 'function') {
      return thread.comments.toArray();
    }

    if (Array.isArray(thread.comments._items)) {
      return thread.comments._items;
    }

    try {
      return Array.from(thread.comments);
    } catch (_error) {
      return [];
    }
  }

  private removeCommentFromThread(
    thread: any,
    commentId: string,
    fallbackIndex = -1
  ): boolean {
    if (!thread) {
      return false;
    }

    if (typeof thread.removeComment === 'function' && commentId) {
      try {
        thread.removeComment(commentId);
        return true;
      } catch (_error) {
        // Fallback to direct mutation when removeComment signature differs.
      }
    }

    const comments = this.getThreadComments(thread);
    if (!comments.length) {
      return false;
    }

    const commentIndexById = commentId
      ? comments.findIndex((comment: any) => {
          const id = comment?.commentId || comment?.id;
          return id === commentId;
        })
      : -1;
    const indexToRemove =
      commentIndexById > -1 ? commentIndexById : fallbackIndex;

    if (indexToRemove < 0 || indexToRemove >= comments.length) {
      return false;
    }

    if (typeof thread.comments?.remove === 'function') {
      try {
        thread.comments.remove(indexToRemove);
        return true;
      } catch (_error) {
        // Continue with immutable fallback.
      }
    }

    const filteredComments = comments.filter(
      (_: any, index: number) => index !== indexToRemove
    );

    thread.comments = filteredComments;
    return true;
  }

  private findCommentByReply(thread: any, reply: any): any {
    if (!thread || !reply) {
      return null;
    }

    const comments = this.getThreadComments(thread);
    if (!comments.length) {
      return null;
    }

    const replyId = reply?.sourceCommentId || reply?.id;

    if (replyId) {
      const byId = comments.find((comment: any) => {
        const id = comment?.commentId || comment?.id;
        return id === replyId;
      });

      if (byId) {
        return byId;
      }
    }

    if (typeof reply?.sourceIndex === 'number' && comments[reply.sourceIndex]) {
      return comments[reply.sourceIndex];
    }

    return null;
  }

  private getPlainTextFromHtml(content: string): string {
    if (!content) {
      return '';
    }

    const parsed = new DOMParser().parseFromString(content, 'text/html');
    return (parsed.body?.textContent || '').trim();
  }

  private wrapTextAsParagraph(content: string): string {
    const escapedContent = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return `<p>${escapedContent}</p>`;
  }

  private getCurrentUserId(): string {
    try {
      const currentUser = JSON.parse(
        localStorage.getItem('currentUser') || '{}'
      );
      return currentUser?.id || currentUser?.username || 'Unknown';
    } catch (_error) {
      return 'Unknown';
    }
  }

  private getCurrentUserInitials(): string {
    try {
      const currentUser = JSON.parse(
        localStorage.getItem('currentUser') || '{}'
      );
      const fullName =
        currentUser?.name || currentUser?.username || currentUser?.id || 'ME';
      return this.getUserInitials(fullName) || 'ME';
    } catch (_error) {
      return 'ME';
    }
  }

  /**
   * Show the panel
   */
  public show(): void {
    this.refreshTrackChangesFromMiniEditor();
    this.isVisible = true;
    this.openedAt = Date.now();
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Hide the panel
   */
  public hide(): void {
    // Store currently focused element before hiding (for WCAG 2.1 Level AA focus restoration)
    if (this.isVisible && document.activeElement instanceof HTMLElement) {
      this.preFocusedElement = document.activeElement;
    }
    this.clearFocusedSuggestionHighlight();
    this.isVisible = false;
    this.activeChangeId = null;
    this.replyingToSuggestionId = null;
    this.replyDraft = '';
    this.changeDetectorRef.markForCheck();
    // Restore focus after panel is hidden
    this.restoreFocus();
  }

  private isClickInsideRHPEditor(element: Element | null): boolean {
    if (!element) {
      return false;
    }
    if (element.parentElement?.id === RHP_EDITOR_ID) {
      return true;
    }
    return this.isClickInsideRHPEditor(element.parentElement);
  }

  @HostListener('document:click', ['$event'])
  public closeOnOutsideClick(event: MouseEvent): void {
    if (!this.isVisible) {
      return;
    }

    // Apply guard only to outside-click detection (not Escape)
    if (
      Date.now() - this.openedAt <
      WkTcCrossLinksPanelComponent.OUTSIDE_CLICK_GUARD_MS
    ) {
      return;
    }

    const panelElement = this.panelWrapperRef?.nativeElement?.querySelector(
      '.wk-tc-cross-links-panel'
    );
    const target = event?.target instanceof Element ? event.target : null;

    if (!panelElement || !target) {
      return;
    }

    // Use composedPath to detect clicks across shadow DOM boundaries
    const composedPath =
      typeof (event as any).composedPath === 'function'
        ? ((event as any).composedPath() as EventTarget[])
        : [];

    // Check if click is inside either the panel or the RHP (wk-info-drawer)
    const clickedInsidePanel = composedPath.includes(panelElement);

    if (clickedInsidePanel) {
      return;
    }

    if (!this.isClickInsideRHPEditor(target) || !target.parentElement) {
      this.hide();
    }
  }

  /**
   * Close panel immediately on Escape key (no 120ms guard for accessibility)
   */
  @HostListener('keydown.escape')
  public closeOnEscape(): void {
    if (this.isVisible) {
      this.hide();
    }
  }

  /**
   * Restore focus to element that had focus before panel was closed (WCAG 2.1 AA compliance)
   */
  private restoreFocus(): void {
    if (
      this.preFocusedElement &&
      this.preFocusedElement instanceof HTMLElement
    ) {
      this.preFocusedElement.focus();
      this.preFocusedElement = null;
    }
  }

  /**
   * Get user initials for avatar
   */
  public getUserInitials(author: string): string {
    if (!author) {
      return '';
    }
    const names = author.split(' ');
    return names
      .map((n) => n.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);
  }

  /**
   * Get avatar color based on author
   */
  public getAvatarColor(author: string): string {
    if (!author) {
      return 'var(--ck-user-avatar-background, #2f6db5)';
    }

    return 'var(--ck-user-avatar-background, #007ac3)';
  }

  public normalizeAsteriskBoldMarkers(content: string): string {
    if (!content) {
      return '';
    }

    // Keep existing **bold** markers untouched, then normalize *bold* markers.
    const escapedDoubleMarkers = content.replace(
      /\*\*/g,
      WkTcCrossLinksPanelComponent.DOUBLE_ASTERISK_PLACEHOLDER
    );
    const withNormalizedSingleMarkers = escapedDoubleMarkers.replace(
      /\*(.+?)\*/g,
      '**$1**'
    );
    const normalizedMarkers = withNormalizedSingleMarkers.replace(
      new RegExp(WkTcCrossLinksPanelComponent.DOUBLE_ASTERISK_PLACEHOLDER, 'g'),
      '**'
    );

    return normalizedMarkers.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  }
}
